import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { RichTextEditor } from '../components/RichTextEditor';
import { ArrowLeft, Send, Image as ImageIcon, Upload, Loader2, X } from 'lucide-react';
import { communityApi } from '../services/communityApi';
import { toast } from 'react-hot-toast';

const POST_TYPES_KEYS = [
  { value: 'discussion', key: 'community.postType.discussion' },
  { value: 'question', key: 'community.postType.question' },
  { value: 'article', key: 'community.postType.article' },
  { value: 'announcement', key: 'community.postType.announcement' },
];

// ─── Gallery images per post type ───────────────────────────────────────────

type GalleryImage = { id: string; url: string; alt: string };

const COMMUNITY_GALLERY: Record<string, GalleryImage[]> = {
  discussion: [
    { id: 'd1', url: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Groepsgesprek' },
    { id: 'd2', url: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bijeenkomst' },
    { id: 'd3', url: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Groepsbijeenkomst buiten' },
    { id: 'd4', url: 'https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Mensen in gesprek' },
    { id: 'd5', url: 'https://images.unsplash.com/photo-1543269664-647b35f78e7b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Workshop overleg' },
    { id: 'd6', url: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Community event' },
    { id: 'd7', url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Teamoverleg' },
    { id: 'd8', url: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Vergaderruimte' },
    { id: 'd9', url: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Team samenwerking' },
    { id: 'd10', url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Vergadering laptop' },
    { id: 'd11', url: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Business bespreking' },
    { id: 'd12', url: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Groep werkt samen' },
  ],
  question: [
    { id: 'q1', url: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Brainstorm ideeën' },
    { id: 'q2', url: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Studeren aantekeningen' },
    { id: 'q3', url: 'https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Notitieblok schrijven' },
    { id: 'q4', url: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Laptop en koffie studeren' },
    { id: 'q5', url: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Boeken kennis' },
    { id: 'q6', url: 'https://images.unsplash.com/photo-1456324504439-367cee3b3c32?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Werken laptop buiten' },
    { id: 'q7', url: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Online meeting vragen' },
    { id: 'q8', url: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Whiteboard sessie' },
    { id: 'q9', url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Denken en reflecteren' },
    { id: 'q10', url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Professional advies' },
    { id: 'q11', url: 'https://images.unsplash.com/photo-1531538606174-0f90ff5dce83?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Presentatie team' },
    { id: 'q12', url: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Zakelijk overleg' },
  ],
  article: [
    { id: 'a1', url: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Schrijven pen papier' },
    { id: 'a2', url: 'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Boeken lezen bibliotheek' },
    { id: 'a3', url: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bibliotheek gangpad' },
    { id: 'a4', url: 'https://images.unsplash.com/photo-1471970394675-613138e45da3?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Tijdschrift lezen' },
    { id: 'a5', url: 'https://images.unsplash.com/photo-1519791883288-dc8bd696e667?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Typen laptop bureau' },
    { id: 'a6', url: 'https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Aantekeningen maken' },
    { id: 'a7', url: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Studeren bureau' },
    { id: 'a8', url: 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Koffie en laptop schrijven' },
    { id: 'a9', url: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bloggen laptop' },
    { id: 'a10', url: 'https://images.unsplash.com/photo-1520085601670-ee14aa5fa3e8?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Schrijfblok pen' },
    { id: 'a11', url: 'https://images.unsplash.com/photo-1503551723145-6c040742065b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Open boek lezen' },
    { id: 'a12', url: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Brainstorm artikel' },
  ],
  announcement: [
    { id: 'n1', url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Evenement feestelijk' },
    { id: 'n2', url: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Presentatie aankondiging' },
    { id: 'n3', url: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Online nieuws' },
    { id: 'n4', url: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Zomerevent aankondiging' },
    { id: 'n5', url: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Conference aankondiging' },
    { id: 'n6', url: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Festival evenement' },
    { id: 'n7', url: 'https://images.unsplash.com/photo-1484318571209-661cf29a69d3?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Groene bijeenkomst' },
    { id: 'n8', url: 'https://images.unsplash.com/photo-1522158637959-30ab6035b3ad?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Sportief event' },
    { id: 'n9', url: 'https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Nieuws update' },
    { id: 'n10', url: 'https://images.unsplash.com/photo-1560523160-754a9e25c68f?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Megafoon nieuws' },
    { id: 'n11', url: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Community nieuws' },
    { id: 'n12', url: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bijeenkomst aankondiging' },
  ],
};

// ─── Gallery picker ──────────────────────────────────────────────────────────

const CommunityGalleryPicker: React.FC<{
  postType: string;
  value: string;
  onChange: (url: string) => void;
}> = ({ postType, value, onChange }) => {
  const { t } = useTranslation('pages');
  const [showGallery, setShowGallery] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const images: GalleryImage[] = COMMUNITY_GALLERY[postType] ?? [];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await communityApi.uploadImage(file);
      onChange(url);
      setShowGallery(false);
      toast.success(t('communityNewPost.photoUploaded'));
    } catch {
      toast.error(t('communityNewPost.uploadFailed'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={handleFileChange}
        disabled={uploading}
      />

      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-gray-200">
          <img src={value} alt={t('communityNewPost.coverPreview')} className="w-full h-48 object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80 transition-colors"
            title={t('communityNewPost.removeImage')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowGallery((v) => !v)}
              className="flex-1 flex items-center justify-center gap-2 h-12 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#0B1F3A] hover:text-[#0B1F3A] transition-colors"
            >
              <ImageIcon className="w-4 h-4" />
              {showGallery ? t('communityNewPost.hideGallery') : t('communityNewPost.chooseFromGallery')}
            </button>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex-1 flex items-center justify-center gap-2 h-12 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#0B1F3A] hover:text-[#0B1F3A] transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {t('communityNewPost.uploadOwnPhoto')}
            </button>
          </div>

          {showGallery && images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              {images.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => {
                    onChange(img.url);
                    setShowGallery(false);
                  }}
                  className="relative aspect-video rounded-md overflow-hidden ring-2 ring-transparent hover:ring-[#0B1F3A] focus:outline-none focus:ring-[#0B1F3A] transition-all"
                  title={img.alt}
                >
                  <img
                    src={img.url}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

export const CommunityNewPostPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedChannel = location.state as { category_id?: string; category_name?: string } | null;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState(preselectedChannel?.category_id || '');
  const [type, setType] = useState('discussion');
  const [coverImage, setCoverImage] = useState('');

  const { data: channels = [] } = useQuery({
    queryKey: ['community-channels'],
    queryFn: communityApi.getChannels,
  });

  const createMutation = useMutation({
    mutationFn: communityApi.createPost,
    onSuccess: (post) => {
      toast.success(t('communityNewPost.postPlaced'));
      navigate(`/community/bericht/${post.id}`);
    },
    onError: (err: any) => {
      const msgs = err?.response?.data?.errors;
      if (msgs?.length) {
        toast.error(msgs[0]);
      } else {
        toast.error(t('community.somethingWentWrong'));
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryId) {
      toast.error(t('communityNewPost.selectChannel'));
      return;
    }
    if (!title.trim() || title.trim().length < 3) {
      toast.error(t('communityNewPost.titleMinLength'));
      return;
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = content;
    const plainText = (tmp.textContent || tmp.innerText || '').trim();
    if (!plainText) {
      toast.error(t('communityNewPost.contentRequired'));
      return;
    }
    createMutation.mutate({
      title: title.trim(),
      content,
      category_id: categoryId,
      type,
      featured_image: coverImage || undefined,
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <button onClick={() => navigate('/community')} className="hover:text-[#0B1F3A] flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> {t('community.title')}
        </button>
        <span>›</span>
        <span className="text-gray-700">{t('communityNewPost.breadcrumb')}</span>
      </div>

      <Card>
        <h1 className="text-xl font-bold text-gray-900 mb-6">{t('communityNewPost.heading')}</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Channel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('communityNewPost.channelLabel')} <span className="text-red-500">*</span>
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
              required
            >
              <option value="">{t('communityNewPost.selectChannelOption')}</option>
              {channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {t(`screening.types.${c.professional_type}`, { defaultValue: c.name })} ({c.member_count} {t('community.members')})
                </option>
              ))}
            </select>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('communityNewPost.postTypeLabel')}</label>
            <div className="flex gap-2 flex-wrap">
              {POST_TYPES_KEYS.map((pt) => (
                <button
                  key={pt.value}
                  type="button"
                  onClick={() => {
                    setType(pt.value);
                    setCoverImage('');
                  }}
                  className={`text-sm px-3 py-1.5 rounded-lg transition-colors border ${
                    type === pt.value
                      ? 'bg-[#0B1F3A] text-white border-[#0B1F3A]'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-[#0B1F3A]'
                  }`}
                >
                  {t(pt.key)}
                </button>
              ))}
            </div>
          </div>

          {/* Cover image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('communityNewPost.coverPhotoLabel')} <span className="text-gray-400 font-normal">({t('communityNewPost.optional')})</span>
            </label>
            <CommunityGalleryPicker
              postType={type}
              value={coverImage}
              onChange={setCoverImage}
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('communityNewPost.titleLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('communityNewPost.titlePlaceholder')}
              maxLength={255}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
              required
            />
          </div>

          {/* Rich text content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('communityNewPost.contentLabel')} <span className="text-red-500">*</span>
            </label>
            <RichTextEditor
              content={content}
              onChange={setContent}
              placeholder={t('communityNewPost.contentPlaceholder')}
              minHeight={250}
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/community')}>
              {t('communityNewPost.cancel')}
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              <Send className="w-4 h-4 mr-2" />
              {t('communityNewPost.submitPost')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
